import React, { useState } from 'react';
import './TutorialPopup.css';

interface TutorialPopupProps {
  onClose: () => void;
}

const TutorialPopup: React.FC<TutorialPopupProps> = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [neverShowAgain, setNeverShowAgain] = useState(false);

  const steps = [
    {
      emoji: '🎨',
      title: 'Welcome to Photo Blender!',
      description: 'A multiplayer guessing game where you upload photos and guess who took each one.',
      color: '#0ea5e9' // Electric blue
    },
    {
      emoji: '📸',
      title: 'Upload Your Photos',
      description: 'Each player uploads 5+ photos. They can be anything - selfies, food, nature, whatever!',
      color: '#ec4899' // Hot pink
    },
    {
      emoji: '🎮',
      title: 'Play & Guess',
      description: 'Photos are shuffled and shown one by one. Guess who took each photo before time runs out!',
      color: '#8b5cf6' // Purple
    },
    {
      emoji: '🏆',
      title: 'Score Points',
      description: 'Correct guesses earn 100 points. Fast guessers get time bonuses. Build streaks for extra points!',
      color: '#84cc16' // Lime
    },
    {
      emoji: '🔒',
      title: 'Your Privacy Matters',
      description: 'All photos are automatically deleted 10 minutes after the game ends. No permanent storage.',
      color: '#f59e0b' // Orange
    }
  ];

  const currentStepData = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    if (neverShowAgain && typeof localStorage !== 'undefined') {
      localStorage.setItem('photoBlenderTutorialSeen', 'true');
    }
    onClose();
  };

  const handleSkip = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('photoBlenderTutorialSeen', 'true');
    }
    onClose();
  };

  return (
    <div className="tutorial-overlay" onClick={handleClose}>
      <div className="tutorial-container" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="tutorial-close" onClick={handleClose} aria-label="Close tutorial">
          ✕
        </button>

        {/* Progress dots */}
        <div className="tutorial-progress">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              style={{
                backgroundColor: index <= currentStep ? currentStepData.color : 'rgba(255, 255, 255, 0.3)'
              }}
            />
          ))}
        </div>

        {/* Step content */}
        <div className="tutorial-content" key={currentStep}>
          <div
            className="tutorial-emoji"
            style={{
              textShadow: `0 0 40px ${currentStepData.color}80, 0 0 20px ${currentStepData.color}40`
            }}
          >
            {currentStepData.emoji}
          </div>
          <h2
            className="tutorial-title"
            style={{ color: currentStepData.color }}
          >
            {currentStepData.title}
          </h2>
          <p className="tutorial-description">{currentStepData.description}</p>
        </div>

        {/* Navigation */}
        <div className="tutorial-navigation">
          <button
            className="tutorial-btn tutorial-btn-secondary"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            style={{ opacity: currentStep === 0 ? 0.3 : 1 }}
          >
            ← Previous
          </button>

          <button
            className="tutorial-btn tutorial-btn-primary"
            onClick={handleNext}
            style={{
              backgroundColor: currentStepData.color,
              boxShadow: `0 0 20px ${currentStepData.color}60, 0 4px 0 ${currentStepData.color}80`
            }}
          >
            {currentStep === steps.length - 1 ? "Let's Play! 🚀" : 'Next →'}
          </button>
        </div>

        {/* Footer options */}
        <div className="tutorial-footer">
          <label className="tutorial-checkbox">
            <input
              type="checkbox"
              checked={neverShowAgain}
              onChange={(e) => setNeverShowAgain(e.target.checked)}
            />
            <span>Don't show this again</span>
          </label>

          <button className="tutorial-skip" onClick={handleSkip}>
            Skip Tutorial
          </button>
        </div>

        {/* Confetti effect on last step */}
        {currentStep === steps.length - 1 && (
          <div className="confetti-container">
            {Array.from({ length: 50 }).map((_, i) => (
              <div
                key={i}
                className="confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  backgroundColor: steps[Math.floor(Math.random() * steps.length)].color
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TutorialPopup;
